import { useEffect, useRef, type RefObject } from 'react'
import { analytics } from '../../utils/analytics'

/**
 * Identity of the thing being played, attached to every video event so an
 * OpenPanel chart can group by video without joining against the database.
 *
 * `id` is the grouping key and must be stable across sessions and deploys;
 * `title` exists purely so the charts are readable at a glance.
 */
export interface VideoTracking {
  /** Stable key: `lesson:<course>/<lesson>`, `trailer:<course>`, `article:<slug>`. */
  id: string
  /** Human-readable name, used as the chart label. */
  title: string
  /** Which surface the player was embedded in. */
  surface: 'lesson' | 'course_trailer' | 'article_hero'
  /** Extra properties merged into every event for this video (course slug, …). */
  props?: Record<string, string>
}

/**
 * Event taxonomy. Four events cover the questions worth asking: how often was a
 * video opened (`video_start`), how far did people get (`video_progress` at the
 * quartiles, `video_complete`), and where did the ones who left drop out
 * (`video_stop`).
 */
export const VIDEO = {
  start: 'video_start',
  progress: 'video_progress',
  complete: 'video_complete',
  stop: 'video_stop',
} as const

/** Quartile milestones, each fired at most once per player mount. */
const MILESTONES = [25, 50, 75] as const

/**
 * A single `timeupdate` step is ~250ms. Anything larger than this is a seek (or
 * a tab that was backgrounded), so it is excluded from watch time. Without the
 * guard, scrubbing to the end would report a full watch.
 */
const MAX_STEP_SECONDS = 2

/**
 * Reports playback of a single video to OpenPanel.
 *
 * Counts are per player mount rather than per play: pausing and resuming does
 * not re-fire `video_start`, so `video_start` really does mean "this video was
 * opened once". Watch time is accumulated from forward playback only, which
 * makes it comparable across people who scrub and people who do not.
 *
 * No-ops entirely when `track` is undefined, and `analytics.capture` is itself a
 * no-op on localhost and for detected bots, so nothing here inflates the numbers.
 */
export function useVideoAnalytics(
  videoRef: RefObject<HTMLVideoElement | null>,
  track?: VideoTracking,
) {
  // Kept in a ref so the effect below depends only on the video's identity: the
  // caller passes a fresh object literal every render, and re-subscribing the
  // media listeners on every render would drop the accumulated watch state.
  const trackRef = useRef(track)
  trackRef.current = track

  const id = track?.id

  useEffect(() => {
    const el = videoRef.current
    if (!el || !id) return

    let watched = 0
    let lastTime = 0
    let started = false
    let ended = false
    const hit = new Set<number>()

    const emit = (event: string, extra?: Record<string, unknown>) => {
      const t = trackRef.current
      if (!t) return
      analytics.capture(event, {
        videoId: t.id,
        videoTitle: t.title,
        surface: t.surface,
        ...t.props,
        ...extra,
      })
    }

    const percent = () => {
      const d = el.duration
      return Number.isFinite(d) && d > 0 ? Math.min(100, (el.currentTime / d) * 100) : 0
    }

    /** Rounded so OpenPanel's numeric breakdowns stay legible. */
    const snapshot = () => ({
      secondsWatched: Math.round(watched),
      percentWatched: Math.round(percent()),
      durationSeconds: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
    })

    const onPlay = () => {
      lastTime = el.currentTime
      if (started) return
      started = true
      emit(VIDEO.start, {
        durationSeconds: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
      })
    }

    const onSeeking = () => {
      lastTime = el.currentTime
    }

    const onTimeUpdate = () => {
      const step = el.currentTime - lastTime
      lastTime = el.currentTime
      if (step > 0 && step < MAX_STEP_SECONDS) watched += step

      const p = percent()
      for (const m of MILESTONES) {
        if (p >= m && !hit.has(m)) {
          hit.add(m)
          emit(VIDEO.progress, { milestone: m, ...snapshot() })
        }
      }
    }

    const onEnded = () => {
      if (ended) return
      ended = true
      emit(VIDEO.complete, snapshot())
    }

    /**
     * Fired when the player goes away with the video unfinished, which is the
     * only place the drop-off point is knowable. Guarded so a page that both
     * hides and unmounts reports once.
     */
    let stopped = false
    const reportStop = () => {
      if (stopped || ended || !started) return
      stopped = true
      emit(VIDEO.stop, snapshot())
    }

    el.addEventListener('play', onPlay)
    el.addEventListener('seeking', onSeeking)
    el.addEventListener('timeupdate', onTimeUpdate)
    el.addEventListener('ended', onEnded)
    window.addEventListener('pagehide', reportStop)

    return () => {
      reportStop()
      el.removeEventListener('play', onPlay)
      el.removeEventListener('seeking', onSeeking)
      el.removeEventListener('timeupdate', onTimeUpdate)
      el.removeEventListener('ended', onEnded)
      window.removeEventListener('pagehide', reportStop)
    }
  }, [videoRef, id])
}
